// src/Pages/Dashboard/Customers.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./Customers.css";

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState(""); // ← NEW: Search state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const userId =
    localStorage.getItem("userId") || sessionStorage.getItem("userId");

  const backendUrl =
    process.env.REACT_APP_BACKEND_URL ||
    (process.env.NODE_ENV === "production"
      ? "https://finance.dev.marsforce.in"
      : "http://localhost:8030");

  const fetchCustomers = async () => {
    if (!userId) {
      setError("Please log in again.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");
      const res = await fetch(
        `${backendUrl}/api/customers/get?userId=${userId}`
      );
      const data = await res.json();

      if (data.success) {
        setCustomers(data.data || []);
      } else {
        setError(data.error || "Failed to load customers");
      }
    } catch (err) {
      setError("Network error. Please check your connection.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, [userId]);

  useEffect(() => {
    window.addEventListener("focus", fetchCustomers);
    return () => window.removeEventListener("focus", fetchCustomers);
  }, []);

  // ← NEW: Filter customers based on search
  const filteredCustomers = customers.filter((cust) => {
    const fullName =
      [cust.firstName, cust.lastName].filter(Boolean).join(" ").trim() ||
      cust.displayName ||
      cust.name ||
      "";

    return (
      fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (cust.companyName || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (cust.email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (cust.workPhone || cust.phone || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    );
  });

  const getFullName = (cust) => {
    return (
      [cust.firstName, cust.lastName].filter(Boolean).join(" ").trim() ||
      cust.displayName ||
      cust.name ||
      "Unnamed Customer"
    );
  };

  if (loading) {
    return (
      <div className="customers-wrapper">
        <div className="customers-container">
          <div className="state-card">
            <p>Loading customers...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="customers-wrapper">
      <div className="customers-container">
        {/* Header with Search */}
        <div className="customers-header">
          <h1>Active Customers ({filteredCustomers.length})</h1>
          <div className="header-actions">
            <input
              type="text"
              placeholder="Search by name, company, email or phone..."
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Link to="/sales/customers/new">
              <button className="new-btn">
                <span>+</span> New Customer
              </button>
            </Link>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="state-card error">
            <p>{error}</p>
          </div>
        )}

        {/* Empty State */}
        {filteredCustomers.length === 0 && !loading && (
          <div className="state-card empty">
            <h3>{searchTerm ? "No customers found" : "No Customers Yet"}</h3>
            <p>
              {searchTerm
                ? `No match for "${searchTerm}"`
                : "Add your first customer to get started."}
            </p>
            <Link to="/sales/customers/new">
              <button className="new-btn">+ Add Customer</button>
            </Link>
          </div>
        )}

        {/* Customers List */}
        {filteredCustomers.length > 0 && (
          <div className="customers-list">
            {/* Desktop Header */}
            <div className="table-header">
              <span></span>
              <span>NAME</span>
              <span>COMPANY</span>
              <span>EMAIL</span>
              <span>PHONE</span>
              <span>RECEIVABLES</span>
              <span>UNUSED CREDITS</span>
            </div>

            {/* Rows */}
            {filteredCustomers.map((cust) => (
              <div
                key={cust._id}
                className="customer-row"
                onClick={() => navigate(`/sales/customers/${cust._id}`)}
              >
                <input type="checkbox" onClick={(e) => e.stopPropagation()} />

                <span data-label="Name" className="customer-name">
                  <Link
                    to={`/sales/customers/${cust._id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="name-link"
                  >
                    {getFullName(cust)}
                  </Link>
                </span>

                <span data-label="Company">{cust.companyName || "-"}</span>
                <span data-label="Email">{cust.email || "-"}</span>
                <span data-label="Phone">
                  {cust.workPhone || cust.phone || "-"}
                </span>
                <span data-label="Receivables" className="amount">
                  ₹{Number(cust.receivables || 0).toFixed(2)}
                </span>
                <span data-label="Credits" className="amount">
                  ₹{Number(cust.unusedCredits || 0).toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Customers;
